import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';

const HeroCarousel = () => {
  const { heroSlides } = useSiteContent();
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const next = useCallback(() => setCurrent((c) => total ? (c + 1) % total : 0), [total]);
  const prev = () => setCurrent((c) => total ? (c - 1 + total) % total : 0);

  useEffect(() => {
    if (!total) return;
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [next, total]);

  const scrollToId = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!total) return <section id="home" className="h-[500px] bg-[#0f2243]" />;
  const slide = heroSlides[current];
  const isFullCover = !!slide.background_mode;
  const showText = slide.show_text !== false; // default true when field is undefined
  const imgSrc = resolveImageUrl(slide.image);

  // Admin can override text sizes per slide with a Tailwind class keyword.
  // Accepts either a preset key (xs/sm/base/lg/xl/2xl/3xl/4xl/5xl/6xl) or a
  // fully-qualified Tailwind class string ("text-3xl md:text-4xl lg:text-5xl").
  const TITLE_PRESETS = {
    sm:  'text-xl md:text-2xl lg:text-3xl',
    md:  'text-2xl md:text-3xl lg:text-4xl',
    lg:  'text-3xl md:text-4xl lg:text-5xl',
    xl:  'text-4xl md:text-5xl lg:text-6xl',
    '2xl': 'text-5xl md:text-6xl lg:text-7xl',
  };
  const SUBTITLE_PRESETS = {
    xs:  'text-sm md:text-base lg:text-lg',
    sm:  'text-base md:text-lg lg:text-xl',
    md:  'text-lg md:text-xl lg:text-2xl',
    lg:  'text-xl md:text-2xl lg:text-3xl',
    xl:  'text-2xl md:text-3xl lg:text-4xl',
  };
  const resolveClass = (val, presets, fallback) => {
    if (!val) return fallback;
    if (typeof val === 'string' && val.includes('text-')) return val;
    return presets[val] || fallback;
  };
  const titleCls = resolveClass(slide.title_size, TITLE_PRESETS, TITLE_PRESETS.lg);
  const subtitleCls = resolveClass(slide.subtitle_size, SUBTITLE_PRESETS, SUBTITLE_PRESETS.md);

  const openLink = () => {
    if (slide.link) window.open(slide.link, '_blank', 'noreferrer');
    else scrollToId('#products');
  };

  const Dots = (
    <div className="flex justify-center gap-2 mt-8">
      {heroSlides.map((_, i) => (
        <button
          key={i} onClick={() => setCurrent(i)}
          aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
          className={`h-2 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'}`}
        />
      ))}
    </div>
  );

  const ArrowBtns = (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); prev(); }}
        aria-label="Previous" data-testid="hero-prev"
        className="hidden sm:flex absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 lg:w-12 lg:h-12 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-colors"
      ><ChevronLeft size={24} /></button>
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); next(); }}
        aria-label="Next" data-testid="hero-next"
        className="hidden sm:flex absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 lg:w-12 lg:h-12 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-colors"
      ><ChevronRight size={24} /></button>
    </>
  );

  // ================= Mode A: Full-Cover Banner =================
  if (isFullCover) {
    return (
      <section
        id="home"
        className="relative w-full overflow-hidden bg-[#0f2243]"
        data-testid="hero-carousel"
      >
        <div
          key={slide.id}
          className="relative w-full aspect-[16/9] sm:aspect-[21/9] lg:aspect-[64/21] max-h-[720px] carousel-slide flex items-center"
          onClick={openLink}
          role="button"
          tabIndex={0}
        >
          {/* Show FULL image on all viewports so any baked-in artwork/text is not cropped */}
          <img
            src={imgSrc}
            alt={slide.title || 'Hero banner'}
            className="absolute inset-0 w-full h-full object-contain object-center bg-[#0f2243]"
            loading="eager"
          />
          {showText && (
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent sm:from-black/70 sm:via-black/40 sm:to-transparent from-black/50 via-black/30 to-black/50" />
          )}
          {showText && (
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-24 text-white w-full">
              <div className="max-w-3xl fade-in-up">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  {slide.badge && (
                    <span className="inline-flex items-center px-3 sm:px-5 py-1 sm:py-2 border-2 border-white/80 rounded-full text-lg sm:text-2xl lg:text-3xl font-extrabold tracking-tight font-[Montserrat]">
                      {slide.badge}
                    </span>
                  )}
                  {slide.edition && <span className="text-[#e60012] text-sm sm:text-lg lg:text-xl font-bold italic">{slide.edition}</span>}
                </div>
                {slide.title && <h1 className={`${titleCls} font-extrabold leading-tight mb-2 font-[Montserrat]`}>{slide.title}</h1>}
                {slide.subtitle && <h2 className={`${subtitleCls} font-bold text-white/90 mb-4 sm:mb-8 font-[Montserrat] leading-snug`}>{slide.subtitle}</h2>}
                <div className="mt-3 sm:mt-6 flex flex-wrap gap-2 sm:gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); openLink(); }}
                    data-testid="hero-cta-products"
                    className="bg-white text-[#0a3d8f] px-4 sm:px-7 py-2 sm:py-3 text-sm sm:text-base rounded-md font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Pelajari Lebih Lanjut
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); scrollToId('#contact'); }}
                    data-testid="hero-cta-contact"
                    className="border-2 border-white text-white px-4 sm:px-7 py-2 sm:py-3 text-sm sm:text-base rounded-md font-semibold hover:bg-white hover:text-[#0a3d8f] transition-colors"
                  >
                    Hubungi Kami
                  </button>
                </div>
              </div>
            </div>
          )}
          {ArrowBtns}
        </div>
        <div className="absolute bottom-4 left-0 right-0 z-20">{Dots}</div>
      </section>
    );
  }

  // ================= Mode B: Classic split layout =================
  return (
    <section
      id="home"
      className="relative w-full overflow-hidden bg-gradient-to-br from-[#1a2942] via-[#0f2243] to-[#1d3556]"
      data-testid="hero-carousel"
    >
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center lg:min-h-[460px]">
          <div key={slide.id} className="fade-in-up text-white order-2 lg:order-1">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <span className="inline-flex items-center px-3 sm:px-5 py-1 sm:py-2 border-2 border-white/80 rounded-full text-lg sm:text-2xl lg:text-3xl font-extrabold tracking-tight font-[Montserrat]">
                {slide.badge}
              </span>
              <span className="text-[#e60012] text-sm sm:text-lg lg:text-xl font-bold italic">{slide.edition}</span>
            </div>
            <h1 className={`${titleCls} font-extrabold leading-tight mb-2 font-[Montserrat]`}>{slide.title}</h1>
            <h2 className={`${subtitleCls} font-bold text-white/90 mb-4 sm:mb-8 font-[Montserrat] leading-snug`}>{slide.subtitle}</h2>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {(slide.features || []).map((f, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 text-gray-800 shadow-lg p-1.5 sm:p-2 text-center fade-in-up"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <span className="text-[9px] sm:text-[10px] font-semibold leading-tight">{f}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 sm:mt-10 flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={openLink}
                data-testid="hero-cta-products"
                className="bg-white text-[#0a3d8f] px-4 sm:px-7 py-2 sm:py-3 text-sm sm:text-base rounded-md font-semibold hover:bg-blue-50 transition-colors"
              >
                Pelajari Lebih Lanjut
              </button>
              <button
                onClick={() => scrollToId('#contact')}
                data-testid="hero-cta-contact"
                className="border-2 border-white text-white px-4 sm:px-7 py-2 sm:py-3 text-sm sm:text-base rounded-md font-semibold hover:bg-white hover:text-[#0a3d8f] transition-colors"
              >
                Hubungi Kami
              </button>
            </div>
          </div>

          <div className="relative flex justify-center items-center order-1 lg:order-2">
            <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-[#e60012] text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg shadow-xl rotate-12 z-10 pulse-new">
              <span className="font-extrabold text-sm sm:text-lg italic font-[Montserrat]">NEW</span>
            </div>
            <a
              href={slide.link || '#products'}
              target={slide.link ? '_blank' : undefined}
              rel="noreferrer"
              data-testid="hero-image-link"
              className="relative w-full max-w-md aspect-[4/3] sm:aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-slate-700 to-slate-900 shadow-2xl block"
            >
              <img
                key={slide.id}
                src={imgSrc}
                alt={slide.title}
                className="w-full h-full object-contain p-3 sm:p-6 carousel-slide bg-white"
              />
            </a>
          </div>
        </div>

        {ArrowBtns}
        {Dots}
      </div>
    </section>
  );
};

export default HeroCarousel;
